import { isMobile, isTablet } from "react-device-detect";
import { IPropsMediaCard } from "../../types";
import * as Styled from "./MediaCard.styled";
import { Clock, StarFill, ChevronDown } from 'react-bootstrap-icons';
import { StarRating } from "../star-rating";
import { useState } from "react";
import { truncateWords, durationFormatter } from "../../libs/utils";


export const MediaCard: React.FC<IPropsMediaCard> = ({
    mediaItem,
    mediaRating,
    cardClick,
    enableRatingControls,
    onWatchClick,
    onRatingSuccess,
    isSignedIn,
    hasEligiblePlan,
    sessionToken,
    isSelected,
    isPlalist,
    enableCompetitionMode
}) => {
    const LABEL_WATCH_NOW = 'Watch Now';
    const LABEL_GET_ACCESS = 'Purchase Plan';
    const LABEL_PURCHASE_PLAN = "Purchase Plan";
    const [isExpanded, setIsExpanded] = useState(false);

    const isTitleTruncated = (mediaItem.title?.length ?? 0) > 30;
    const isDescTruncated = (mediaItem.description?.length ?? 0) > 90;
    const hasOverflow = isTitleTruncated || isDescTruncated;

    /**
     * @function handlePosterOver
     * @description Poster mouseenter event handler.
     * @param {Object} evt
     */
    function handlePosterOver(evt: React.MouseEvent<any>): void {
        evt.currentTarget.focus();
        let targetParent = evt.currentTarget.closest('.media-card');
        let targetPoster = targetParent?.querySelector('.media-poster');
        let targetAncillary = targetParent?.querySelector('.media-ancillary');
        targetParent?.classList.add('hover-intent');
        targetPoster?.classList.add('hover-intent');
        targetAncillary?.classList.add('hover-intent');
    }

    /**
     * @function handlePosterOut
     * @description Poster mouseleave event handler.
     * @param {Object} evt
     */
    function handlePosterOut(evt: React.MouseEvent<any>): void {
        let targetParent = evt.currentTarget.closest('.media-card');
        let targetPoster = targetParent?.querySelector('.media-poster');
        let targetAncillary = targetParent?.querySelector('.media-ancillary');
        targetParent?.classList.remove('hover-intent');
        targetPoster?.classList.remove('hover-intent');
        targetAncillary?.classList.remove('hover-intent');
    }

    /**
     * @function handlePosterFocus
     * @description Poster focus event handler.
     * @param {Object} evt
     */
    function handlePosterFocus(evt: React.FocusEvent<any>): void {
        let targetParent = evt.currentTarget.closest('.media-card');
        let targetPoster = targetParent?.querySelector('.media-poster');
        let targetAncillary = targetParent?.querySelector('.media-ancillary');
        targetParent?.classList.add('hover-intent');
        targetPoster?.classList.add('hover-intent');
        targetAncillary?.classList.add('hover-intent');
    }

    /**
     * @function handlePosterBlur
     * @description Poster blur event handler.
     * @param {Object} evt
     */
    function handlePosterBlur(evt: React.FocusEvent<any>) {
        if (!evt.currentTarget.parentNode?.parentNode?.contains(evt.relatedTarget)) {
            let targetParent = evt.currentTarget.closest('.media-card');
            let targetPoster = targetParent?.querySelector('.media-poster');
            let targetAncillary = targetParent?.querySelector('.media-ancillary');
            targetParent?.classList.remove('hover-intent');
            targetPoster?.classList.remove('hover-intent');
            targetAncillary?.classList.remove('hover-intent');
        }
    }

    /**
     * @function mobileClassCheck
     * @description Check if device is mobile or tablet and return className.
     * @returns {String}
     */
    function mobileClassCheck() {
        return (isMobile || isTablet) ? "is-mobile" : "is-not-mobile";
    }

    const shouldShowRating = !enableCompetitionMode;
    const canRate = enableRatingControls && isSignedIn && hasEligiblePlan;
    const matchedRating = mediaRating?.mediaId === mediaItem?.mediaId ? mediaRating : undefined;

    return (
        <Styled.MediaCard className={`media-card ${mobileClassCheck()}`}>

            <Styled.MediaCardWrapper
                className={`media-card--wrapper ${mobileClassCheck()}`} isSelected={isSelected}>

                <Styled.MediaPoster className={`media-poster ${mobileClassCheck()}`}>

                    <Styled.MediaPosterLink
                        onClick={() => cardClick(mediaItem)}
                        onMouseEnter={(evt) => handlePosterOver(evt)}
                        onMouseLeave={(evt) => handlePosterOut(evt)}
                        onFocus={(evt) => handlePosterFocus(evt)}
                        onBlur={(evt) => handlePosterBlur(evt)}
                        className="media-poster--link"
                        role="button"
                        tabIndex={0}>
                    </Styled.MediaPosterLink>

                    <Styled.MediaPosterArt className="media-poster--art">
                        <Styled.MediaPosterImage
                            src={mediaItem.thumbnail}
                            className={`media-poster--image ${mobileClassCheck()}`}
                            alt="MediaCard Thumbnail"></Styled.MediaPosterImage>
                    </Styled.MediaPosterArt>

                </Styled.MediaPoster>

                <Styled.MediaAncillary
                    onMouseEnter={(evt) => handlePosterOver(evt)}
                    onMouseLeave={(evt) => handlePosterOut(evt)}
                    className={`media-ancillary ${mobileClassCheck()}`}>

                    <Styled.TrailerSection>
                        <Styled.TrailerButton onClick={() => onWatchClick(mediaItem)}>
                            {
                                !!mediaItem.planDetails?.length
                                    ? isSignedIn
                                        ? hasEligiblePlan
                                            ? LABEL_WATCH_NOW
                                            : LABEL_PURCHASE_PLAN
                                        : LABEL_GET_ACCESS
                                    : LABEL_WATCH_NOW
                            }
                            <Styled.StyledPlayFill size={20} />
                        </Styled.TrailerButton>

                        {hasOverflow && (
                            <Styled.IconWrapper onClick={() => setIsExpanded(prev => !prev)}>
                                <ChevronDown
                                    color="currentColor"
                                    size={20}
                                    style={{ transition: 'transform 0.2s', transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)' }}
                                />
                            </Styled.IconWrapper>
                        )}

                    </Styled.TrailerSection>

                    {mediaItem.title && (
                        <Styled.MediaMetadataPlaylist>
                            {isExpanded ? mediaItem.title : truncateWords(mediaItem.title, 30)}
                        </Styled.MediaMetadataPlaylist>
                    )}

                    {mediaItem.description && (
                        <Styled.MediaSynopsisModal title={mediaItem?.description}>
                            {isExpanded ? mediaItem.description : truncateWords(mediaItem?.description, 90)}
                        </Styled.MediaSynopsisModal>
                    )}

                    {
                        mediaItem.genre && (<Styled.GenrePills>
                            <Styled.Genre key={mediaItem.genre}>{mediaItem.genre}</Styled.Genre>
                        </Styled.GenrePills>)
                    }

                    {(mediaItem.duration && shouldShowRating && matchedRating) && <Styled.InfoRow>
                        <Styled.InfoItemGroup>
                            {
                                mediaItem.duration && (
                                    <Styled.InfoItem>
                                        <Clock color="currentColor" />
                                        <div>{durationFormatter(mediaItem.duration)}</div>
                                    </Styled.InfoItem>
                                )
                            }
                            {(shouldShowRating && matchedRating) && (
                                <Styled.InfoItem>
                                    <StarFill color="currentColor" size={20} />
                                    <span style={{ fontWeight: "bold" }}>{(matchedRating?.rating ?? 0)} /5</span>
                                </Styled.InfoItem>
                            )}
                        </Styled.InfoItemGroup>
                    </Styled.InfoRow>}

                    {canRate && (
                        <Styled.MediaRatingPlaylist>
                            <StarRating
                                mediaItem={mediaItem}
                                mediaRating={mediaRating?.rating ?? 0}
                                sessionToken={sessionToken}
                                onRatingSuccess={() => {
                                    onRatingSuccess()
                                }}
                            />
                        </Styled.MediaRatingPlaylist>
                    )}

                    {((mediaItem.year && mediaItem.year.length > 0) ||
                        (mediaItem.duration && mediaItem.duration.length > 0) ||
                        (mediaItem.audience && mediaItem.audience.length > 0) ||
                        (mediaItem.viewerRating && mediaItem.audience.length > 0)) &&
                        <Styled.MediaMetadataPlaylist>
                            {(mediaItem.year && mediaItem.year.length > 0) &&
                                <span className="release-year">{mediaItem.year}</span>
                            }
                            {(mediaItem.duration && mediaItem.duration.length > 0) &&
                                <span className="duration">{durationFormatter(mediaItem.duration)}</span>
                            }
                            {(mediaItem.audience && mediaItem.audience.length > 0) &&
                                <span className="mpaa-rating"><span>{mediaItem.audience}</span></span>
                            }
                            {(mediaItem.viewerRating && mediaItem.audience.length > 0) &&
                                <span className="viewer-rating">
                                    <span className="viewer-rating--filled">&#9733;&#9733;&#9733;</span>
                                    <span className="viewer-rating--empty">&#9734;&#9734;</span>
                                </span>
                            }
                        </Styled.MediaMetadataPlaylist>
                    }

                    {((mediaItem.directors && mediaItem.directors.length > 0) ||
                        (mediaItem.producers && mediaItem.producers.length > 0)) &&
                        <Styled.MediaCrewModal className="media-crew">
                            {(mediaItem.directors && mediaItem.directors.length > 0) &&
                                <Styled.CrewModalItem><span>Directed by:</span> {mediaItem.directors.replace(/,(?=[^\s])/g, ', ')}</Styled.CrewModalItem>
                            }
                            {(mediaItem.producers && mediaItem.producers.length > 0) &&
                                <Styled.CrewModalItem><span>Produced by:</span> {mediaItem.producers.replace(/,(?=[^\s])/g, ', ')}</Styled.CrewModalItem>
                            }
                        </Styled.MediaCrewModal>
                    }

                </Styled.MediaAncillary>

            </Styled.MediaCardWrapper>

        </Styled.MediaCard>
    );
};
